import { Request, Response, RequestHandler } from 'express';
import { injectable, inject } from 'inversify';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';
import { AggregateCommandService } from '../../application/services/AggregateCommandService';
import { AggregateQueryService } from '../../application/services/AggregateQueryService';
import { CreateAggregateRequestSchema, UpdateAggregateRequestSchema, GetAggregatesQuerySchema } from '../dto/AggregateRequestDTOs';
import { validateRequest, validateAggregateParams, validateContextAggregateParams, createErrorResponse } from '../middleware/ValidationMiddleware';
import { 
  createSuccessResponse, 
  createErrorResponse as createStandardErrorResponse, 
  BUSINESS_ACTIONS, 
  DOMAIN_NAMES 
} from '@swoft/core';

/**
 * Aggregate Controller
 * 
 * RESTful API controller for aggregate operations.
 * Binds ONLY to AggregateApplicationService - no direct domain access.
 * 
 * Enforces Clean Architecture by maintaining strict API boundaries.
 */
@injectable()
export class AggregateController {
    constructor(
        @inject(DDD_SYMBOLS.AggregateCommandService)
        private readonly aggregateCommandService: AggregateCommandService,
        @inject(DDD_SYMBOLS.AggregateQueryService)
        private readonly aggregateQueryService: AggregateQueryService
    ) {}

    /**
     * Create a new aggregate
     * POST /aggregates
     */
    async createAggregate(req: Request, res: Response): Promise<void> {
        try {
            // Validate request body
            const validatedRequest = CreateAggregateRequestSchema.parse(req.body);
            
            // Transform DTO to command payload
            const commandPayload = {
                boundedContextId: validatedRequest.boundedContextId,
                name: validatedRequest.name,
                description: validatedRequest.description,
                responsibility: validatedRequest.purpose, // Map purpose to responsibility
                rootEntity: {
                    name: validatedRequest.structure.rootEntity.name,
                    description: validatedRequest.structure.rootEntity.description,
                    properties: [] // Initialize empty properties array
                }
            };
            
            const result = await this.aggregateCommandService.createAggregate(commandPayload);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.CREATE_AGGREGATE,
                DOMAIN_NAMES.DDD,
                'command'
            );
            
            res.status(201).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'AGGREGATE_CREATE_FAILED',
                'Failed to create aggregate',
                BUSINESS_ACTIONS.DDD.CREATE_AGGREGATE,
                DOMAIN_NAMES.DDD,
                'command',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get all aggregates with optional filtering
     * GET /aggregates
     */
    async getAggregates(req: Request, res: Response): Promise<void> {
        try {
            // Validate query parameters
            const validatedQuery = GetAggregatesQuerySchema.parse(req.query);
            
            const result = await this.aggregateQueryService.listAggregates(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    aggregates: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'AGGREGATE_LIST_FAILED',
                'Failed to retrieve aggregates',
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get a specific aggregate by ID
     * GET /aggregates/:aggregateId
     */
    async getAggregate(req: Request, res: Response): Promise<void> {
        try {
            const { aggregateId } = req.params;
            
            const result = await this.aggregateQueryService.getAggregate(aggregateId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.GET_AGGREGATE,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'AGGREGATE_GET_FAILED',
                'Failed to retrieve aggregate',
                BUSINESS_ACTIONS.DDD.GET_AGGREGATE,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get aggregates for a specific bounded context
     * GET /bounded-contexts/:contextId/aggregates
     */
    async getAggregatesByBoundedContext(req: Request, res: Response): Promise<void> {
        try {
            const { contextId } = req.params;
            const validatedQuery = GetAggregatesQuerySchema.parse({
                ...req.query,
                boundedContextId: contextId
            });
            
            const result = await this.aggregateQueryService.listAggregates(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    aggregates: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'BOUNDED_CONTEXT_AGGREGATE_LIST_FAILED',
                'Failed to retrieve bounded context aggregates',
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get aggregates for a specific domain
     * GET /domains/:domainId/aggregates
     */
    async getAggregatesByDomain(req: Request, res: Response): Promise<void> {
        try {
            const { domainId } = req.params;
            const validatedQuery = GetAggregatesQuerySchema.parse({
                ...req.query,
                domainId
            });
            
            const result = await this.aggregateQueryService.listAggregates(validatedQuery);
            
            const response = createSuccessResponse(
                {
                    aggregates: result,
                    total: result.length
                },
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const errorResponse = createStandardErrorResponse(
                'DOMAIN_AGGREGATE_LIST_FAILED',
                'Failed to retrieve domain aggregates',
                BUSINESS_ACTIONS.DDD.LIST_AGGREGATES,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(400).json(errorResponse);
        }
    }

    /**
     * Get aggregate design view with patterns and decisions
     * GET /aggregates/:aggregateId/design
     */
    async getAggregateDesign(req: Request, res: Response): Promise<void> {
        try {
            const { aggregateId } = req.params;
            
            const result = await this.aggregateQueryService.getAggregateAnalysis(aggregateId);
            
            const response = createSuccessResponse(
                result,
                BUSINESS_ACTIONS.DDD.GET_AGGREGATE_DESIGN,
                DOMAIN_NAMES.DDD,
                'query'
            );
            
            res.status(200).json(response);
        } catch (error: any) {
            const statusCode = error.message?.includes('not found') ? 404 : 400;
            const errorResponse = createStandardErrorResponse(
                'AGGREGATE_DESIGN_FAILED',
                'Failed to retrieve aggregate design',
                BUSINESS_ACTIONS.DDD.GET_AGGREGATE_DESIGN,
                DOMAIN_NAMES.DDD,
                'query',
                error instanceof Error ? error.message : 'Unknown error'
            );
            res.status(statusCode).json(errorResponse);
        }
    }

    /**
     * Get middleware for aggregate parameter validation
     */
    static getValidationMiddleware(): {
        validateAggregateParams: RequestHandler;
        validateContextAggregateParams: RequestHandler;
        validateCreateAggregate: RequestHandler;
        validateUpdateAggregate: RequestHandler;
        validateGetAggregatesQuery: RequestHandler;
    } {
        return {
            validateAggregateParams,
            validateContextAggregateParams,
            validateCreateAggregate: validateRequest({ body: CreateAggregateRequestSchema }),
            validateUpdateAggregate: validateRequest({ body: UpdateAggregateRequestSchema }),
            validateGetAggregatesQuery: validateRequest({ query: GetAggregatesQuerySchema })
        };
    }
}